import {RoomEvent} from 'livekit-client';
import {useEffect, useRef} from "react";
import {notifyBugsnag} from "@common/utils/initBugsnag";
import {useRoomContext} from "@livekit/components-react";

// Stale chunk-set TTL. If chunks of a transmission go missing (e.g. publisher
// disconnects mid-send), drop the partial set after this delay so we don't
// leak memory while keeping enough headroom for slow networks.
const CHUNK_TTL_MS = 30 * 1000
const CHUNK_TYPE = "__chunk"

export const useLiveKitEventHandler = (eventHandlers, uint8ArrayToString) => {
    const room = useRoomContext()
    // chunk_id -> { total, received: Map<index, string>, timer }
    const partialChunksRef = useRef(new Map())

    // eventHandlers is rebuilt upstream on every Redux update of its inputs
    // (user, liveSessionId, checkpointConversation...). Routing the dispatch
    // through a ref keeps the DataReceived listener bound once per room
    // instead of re-binding on each of those updates — re-bindings used to
    // wipe in-flight chunk state mid-live.
    const eventHandlersRef = useRef(eventHandlers)
    eventHandlersRef.current = eventHandlers

    useEffect(() => {
        if (!room) return;

        const clearChunkSet = (chunkId) => {
            const entry = partialChunksRef.current.get(chunkId)
            if (entry?.timer) clearTimeout(entry.timer)
            partialChunksRef.current.delete(chunkId)
        }

        const dispatchEvent = (event, participant) => {
            const handler = eventHandlersRef.current[event.type];
            if (handler) {
                handler(event, participant);
            } else {
                console.warn(`No handler found for event type: ${event.type}`);
            }
        }

        const handleDataReceived = (payload, participant, kind) => {
            try {
                const event = JSON.parse(uint8ArrayToString(payload));

                if (event?.type === CHUNK_TYPE) {
                    const { chunk_id, chunk_index, chunk_total, chunk_payload } = event
                    if (
                        typeof chunk_id !== "string" ||
                        typeof chunk_index !== "number" ||
                        typeof chunk_total !== "number" ||
                        typeof chunk_payload !== "string"
                    ) {
                        console.warn("Malformed chunk envelope", event)
                        return
                    }

                    let entry = partialChunksRef.current.get(chunk_id)
                    if (!entry) {
                        entry = {
                            total: chunk_total,
                            received: new Map(),
                            timer: setTimeout(() => clearChunkSet(chunk_id), CHUNK_TTL_MS),
                        }
                        partialChunksRef.current.set(chunk_id, entry)
                    }
                    entry.received.set(chunk_index, chunk_payload)

                    if (entry.received.size < entry.total) return

                    // All chunks present — reassemble in order and dispatch.
                    let assembled = ""
                    for (let i = 0; i < entry.total; i++) {
                        const piece = entry.received.get(i)
                        if (piece === undefined) {
                            // Hole — abandon the set; TTL will clear leftovers.
                            return
                        }
                        assembled += piece
                    }
                    clearChunkSet(chunk_id)
                    try {
                        const assembledEvent = JSON.parse(assembled)
                        dispatchEvent(assembledEvent, participant)
                    } catch (parseError) {
                        console.error('Failed to parse assembled chunked JSON:', parseError);
                        notifyBugsnag("Failed to parse assembled chunked JSON: " + parseError?.toString())
                    }
                    return
                }

                dispatchEvent(event, participant);
            } catch (error) {
                console.error('Failed to parse JSON:', error);
                notifyBugsnag("Failed to parse JSON: " + error?.toString())
            }
        };

        room.on(RoomEvent.DataReceived, handleDataReceived);

        return () => {
            room.off(RoomEvent.DataReceived, handleDataReceived);
            // Flush chunk reassembly state — only fires when the room
            // instance changes or the hook unmounts.
            partialChunksRef.current.forEach((entry) => {
                if (entry?.timer) clearTimeout(entry.timer)
            })
            partialChunksRef.current.clear()
        };
    }, [room]);
};
